import { useState, useCallback, useEffect } from 'react';
import {
  getPropertyDisplayLabel,
  getChildValue,
  IPropertyChild,
} from 'components/property-editors/utils/ProviderNodeUtils';
import { IEditorProps } from './Editor';
import { Checkbox } from '../inputs/Checkbox';
import { useEditorId } from './hooks/useEditorId';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { EditorLabel } from './EditorLabel';
import './EditorCheckbox.less';
import { generateGuid } from 'utils/Guid';

interface IEditorCheckboxProps extends IEditorProps {
  nullable?: boolean;
}

enum CheckboxState {
  UNCHECKED,
  INDETERMINATE,
  CHECKED,
}

/**
 * A Checkbox consuming a property child model.
 *
 * Copyright (c) 2019 Decisions ALl Rights Reserved
 */
export function EditorCheckbox({ autoFocus, child, id, nullable, onChange, path }: IEditorCheckboxProps) {
  [id] = useState(useEditorId(path, id)?.[0] + generateGuid());
  const validation = getEditorValidationSummary(child);
  const value = getInitialValue(child, nullable);
  const [checkState, setCheckState] = useState<CheckboxState>(value);

  useEffect(() => {
    setCheckState(value);
  }, [value]);

  const changeHandler = useCallback(() => {
    const nextState = getNextState(checkState, nullable);
    setCheckState(nextState);
    onChange(getValue(nextState), path);
  }, [checkState, nullable, onChange, path]);

  return (
    <EditorLabel editor={child} path={path} htmlFor={id} doNotWrap hideInputLabel={true}>
      <Checkbox
        key={id}
        id={id}
        autoFocus={autoFocus}
        data-testid={id}
        label={getPropertyDisplayLabel(child)}
        checked={isChecked(checkState)}
        onChange={changeHandler}
        indeterminate={isIndeterminate(checkState)}
        validation={validation}
        className="dpc-editor-checkbox"
      />
    </EditorLabel>
  );
}

function isChecked(state: CheckboxState) {
  return state === CheckboxState.CHECKED;
}

function isIndeterminate(state: CheckboxState) {
  return state === CheckboxState.INDETERMINATE;
}

function getInitialValue(child: IPropertyChild, nullable?: boolean) {
  const value = getChildValue(child);
  return value === null && nullable
    ? CheckboxState.INDETERMINATE
    : value
    ? CheckboxState.CHECKED
    : CheckboxState.UNCHECKED;
}

function getNextState(state: CheckboxState, nullable?: boolean) {
  if (nullable) {
    state++;
    return state > 2 ? 0 : state;
  } else {
    return state === 0 ? 2 : 0;
  }
}

function getValue(state: CheckboxState) {
  return state === CheckboxState.INDETERMINATE ? null : state === CheckboxState.CHECKED ? true : false;
}
